// src/i18n/index.js — Itemzo customer frontend i18n (Phase 1).
//
// Languages:
//   az → default (Azerbaijani stays default everywhere)
//   en → secondary
//
// Design notes:
//   - Resources are bundled (no async backend) so the very first paint is
//     already translated — no flicker, no Suspense needed.
//   - Selected language persists in localStorage under `itemzo_lang`.
//   - document.documentElement.lang is kept in sync on init + change.
//   - Only the `common` namespace is loaded in Phase 1.

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import azCommon from '../locales/az/common.json'
import enCommon from '../locales/en/common.json'

export const SUPPORTED_LANGS = ['az', 'en']
export const DEFAULT_LANG = 'az'
export const LANG_STORAGE_KEY = 'itemzo_lang'

const readStoredLang = () => {
    try {
        const stored = localStorage.getItem(LANG_STORAGE_KEY)
        if (stored && SUPPORTED_LANGS.includes(stored)) return stored
    } catch (_) { /* ignore */ }
    return DEFAULT_LANG
}

const initialLang = readStoredLang()

i18n
    .use(initReactI18next)
    .init({
        resources: {
            az: { common: azCommon },
            en: { common: enCommon },
        },
        lng: initialLang,
        fallbackLng: DEFAULT_LANG,
        supportedLngs: SUPPORTED_LANGS,
        ns: ['common'],
        defaultNS: 'common',
        interpolation: { escapeValue: false }, // React already escapes
        react: { useSuspense: false },
    })

// Keep <html lang> in sync (initial + on every change).
const applyHtmlLang = (lng) => {
    try { document.documentElement.lang = lng } catch (_) { /* ignore */ }
}
applyHtmlLang(i18n.language || initialLang)
i18n.on('languageChanged', applyHtmlLang)

// Persist the chosen language. Centralised here so any caller of
// i18n.changeLanguage (e.g. the header switcher) stays consistent.
export const changeLanguage = (lng) => {
    if (!SUPPORTED_LANGS.includes(lng)) return
    try { localStorage.setItem(LANG_STORAGE_KEY, lng) } catch (_) { /* ignore */ }
    i18n.changeLanguage(lng)
}

export default i18n
